import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Truck,
  DollarSign,
  ClipboardList,
  Building2,
  Factory,
  Users,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Search,
  Brain,
  Database,
  Target,
  Clock,
  TrendingUp,
  Layers3,
  ShieldCheck,
  FileText,
  Warehouse,
  ArrowLeftRight,
  Calculator,
  Eye,
  Briefcase,
  HardHat,
  Wrench,
  CircuitBoard,
  Globe,
  MapPin,
  ChevronRight,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "programs",
    num: "01",
    label: "Programs & Planning",
    sub: "The long-term acquisition strategy. How many pieces per MW.",
    Icon: Target,
    color: "from-sky-500 to-cyan-400",
    lightBg: "bg-sky-50",
    lightBorder: "border-sky-200",
    dotColor: "bg-sky-500",
    summary:
      "Before a single PO is issued, the program defines the bill of materials for every megawatt. A 72MW DEC3.0 Wholesale build requires specific quantities of generators, switchgear, PDUs, UPS systems, cooling units, and busway — down to the SKU. Today this lives in SharePoint documents and tribal knowledge. Tomorrow it's a governed, queryable BOM that every downstream process inherits. When Design Management changes a spec, every affected project's Production Planner updates automatically.",
    bullets: [
      "Design program BOM: every equipment type and quantity per MW, per program variant",
      "Vendor pre-assessment and Partner Quality factory evaluation tracking",
      "RFP generation, distribution, and bid leveling — side-by-side vendor comparison",
      "Buyout plan approval workflow: Leadership → Compliance → SLT → BOD funding",
      "Program-to-project mapping: one program feeds 23 active projects simultaneously",
      "Design change propagation: spec change in the program cascades to every affected planner",
    ],
  },
  {
    id: "production",
    num: "02",
    label: "Production Tracking",
    sub: "Every unit. Every vendor. Every project. One planner.",
    Icon: ClipboardList,
    color: "from-amber-500 to-orange-400",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "The Production Planner is the canonical tracker — one row per physical unit, always QTY 1, from PO issuance through manufacturing, shipping, delivery, and installation. Today there are separate Smartsheet planners per vendor, manually maintained, with no cross-vendor visibility. The system explodes every PO line into individual unit rows, tracks manufacturing status with vendor-reported dates, computes on-time performance, and gives every stakeholder — internal and external — exactly the columns they're authorized to see.",
    bullets: [
      "PO explosion: a PO line for QTY 40 becomes 40 individual tracked units",
      "18 vendors, 23 active projects — one unified view with cross-vendor search",
      "Manufacturing status pipeline: not started → in production → QC → ready to ship → shipped → delivered → installed → commissioned",
      "Vendor portal: each vendor sees only their units, only their authorized columns",
      "Contractual vs. actual ship dates with automated on-time performance scoring",
      "Cross-project visibility: a Director sees every unit across every project in one query",
      "Vendor-specific data fields captured without polluting the canonical schema",
    ],
  },
  {
    id: "reallocations",
    num: "03",
    label: "Reallocations",
    sub: "Move equipment between projects without losing financial control.",
    Icon: ArrowLeftRight,
    color: "from-violet-500 to-fuchsia-500",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-500",
    summary:
      "Reallocations are the single most complex operation in OF&E. Equipment ordered for Project A gets redirected to Project B — sometimes mid-manufacturing, sometimes mid-shipment, sometimes from storage. Every reallocation triggers a cascade: the Production Planner updates line-by-line, both project schedule trackers adjust, both financial trackers recalculate, and journal entries must move costs from one project budget to another. Today this is manual, error-prone, and the #1 source of financial reconciliation pain. The system orchestrates the entire cascade from a single reallocation directive.",
    bullets: [
      "Multi-step workflow: propose → informal vendor review → vendor approve → PM notify → execute",
      "Line-level tracking: every unit in the reallocation tracked individually with before/after allocation",
      "AI-powered journal entry identification: the system determines which PO-lines and costs need JE to which project",
      "Cascade to schedule trackers: ROJ dates, delivery plans, and gap analysis update automatically",
      "Cascade to financial trackers: budget allocations, exposure amounts, and PFR codes realign",
      "Full audit trail: who proposed, who approved, when executed, which units moved where",
    ],
  },
  {
    id: "financials",
    num: "04",
    label: "Financial Control",
    sub: "Every dollar tracked to a unit. Every journal entry automated.",
    Icon: DollarSign,
    color: "from-emerald-500 to-teal-400",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-500",
    summary:
      "Financial tracking in OF&E isn't accounting — it's project cost control across 23 simultaneous builds with equipment constantly reallocating between them. Every PO line must map to a unit, every unit to a project budget, every reallocation to a journal entry. Today, PMs manually maintain financial trackers, finance teams spend 30-80% of their time reconciling journal entries, and exposure (future commitments) is tracked in people's heads. The system auto-populates financial trackers from Production Planners and Oracle, tracks exposure as a first-class field, and uses AI to identify and propose journal entries after every reallocation.",
    bullets: [
      "Auto-populated from Production Planners + Oracle PO data — no manual re-entry",
      "Unit price enforcement: no lump sums, every cost tied to a countable unit",
      "Exposure tracking: PMs enter future commitments directly, visible in project totals",
      "AI journal entry identification: after any reallocation, the system proposes JEs with from/to project and amounts",
      "Human-in-the-loop JE approval: PM reviews each proposed JE before submission to Oracle",
      "Change order workflow: technical review → quote → budget determination → CM notification",
      "ALL PO lines allocated — including freight, storage, and non-product items — enabling full reallocation tracking",
    ],
  },
  {
    id: "logistics",
    num: "05",
    label: "Logistics & Delivery",
    sub: "From factory floor to data center door.",
    Icon: Truck,
    color: "from-blue-500 to-cyan-500",
    lightBg: "bg-blue-50",
    lightBorder: "border-blue-200",
    dotColor: "bg-blue-500",
    summary:
      "Equipment moves through a complex physical chain: manufacturer → vendor storage → 3PL warehouse → job site → specific building location. Each handoff is a potential failure point — missed deliveries, damaged equipment, wrong destination, lost units. The system tracks every shipment, manages 3PL storage locations and insurance, enforces 48-hour call-ahead requirements, and handles the receiving workflow including damage assessment, acceptance decisions, and vendor remediation coordination.",
    bullets: [
      "Storage location directory: 3PL facilities, integrator warehouses, vendor staging areas — all with insurance tracking",
      "Shipment tracking by project, vendor, or individual unit with delivery status pipeline",
      "48-hour call-ahead enforcement: automated alerts when deliveries approach without confirmation",
      "Receiving workflow: visual inspection → damage assessment → accept/reject decision → remediation if needed",
      "3PL inventory sync: automated movement tracking from storage to site",
      "Freight PO tracking: every shipment's freight cost allocated to the correct project budget",
    ],
  },
  {
    id: "cx",
    num: "06",
    label: "Commissioning & Closeout",
    sub: "From delivered to operational. The last mile.",
    Icon: Wrench,
    color: "from-rose-500 to-pink-400",
    lightBg: "bg-rose-50",
    lightBorder: "border-rose-200",
    dotColor: "bg-rose-500",
    summary:
      "Procurement's job doesn't end at delivery. The system bridges the gap between 'equipment delivered' and 'equipment operational' — tracking commissioning milestones, managing CxAlloy integration for installation status, handling scope changes discovered during Cx, and collecting closeout documentation (as-builts, O&Ms, warranty certificates) linked to specific serial numbers. The monthly Cx Look-Ahead ensures vendors and PMs see the same milestone timeline.",
    bullets: [
      "Cx Look-Ahead: monthly milestone view across all projects — L2, L3, L4, RVT, IST, RFS dates",
      "CxAlloy integration: bidirectional status sync for installation and commissioning events",
      "Issue tracking with escalation chain: OFE PM → Partner Quality → Leadership",
      "Scope gap identification: when Cx discovers something missing, trigger change order process",
      "Closeout documentation: as-builts, O&Ms, warranty certificates — tagged 'latest', linked to serial numbers",
      "Post-delivery vendor coordination for punch list items and remediation",
    ],
  },
];

const rescueSteps = [
  {
    id: 0,
    label: "Reallocation Initiated",
    agent: null,
    description: "Director initiates reallocation: 14 PDUs ordered for PHX-03 need to move to ATL-07. ATL-07 is 6 weeks ahead of schedule; PHX-03 slipped 4 months.",
    badges: [
      { label: "14 Units Affected", status: "amber" },
      { label: "2 Projects Impacted", status: "amber" },
      { label: "JEs Unknown", status: "red" },
    ],
    detail: null,
  },
  {
    id: 1,
    label: "Unit Mapping",
    agent: "📋 Production Planner scanning...",
    description: "System identifies all 14 units, their current manufacturing status, PO lines, and financial allocations.",
    badges: [
      { label: "14 Units Mapped", status: "green" },
      { label: "2 Projects Impacted", status: "amber" },
      { label: "JEs Unknown", status: "red" },
    ],
    detail: "PO-4892 Lines 3-16: 14× Eaton 93PM UPS, 500kVA. Currently in production at Eaton Raleigh facility. 8 units QC complete, 6 in final assembly. Contractual ship: Aug 15. All currently budgeted to PHX-03 under Task 4400, Expenditure Type: PFR-ELC-UPS.",
  },
  {
    id: 2,
    label: "Vendor Review",
    agent: "🏭 Vendor portal notified...",
    description: "Informal notice sent to Eaton. Vendor confirms reallocation is feasible — shipping labels and documentation can redirect to ATL-07.",
    badges: [
      { label: "14 Units Mapped", status: "green" },
      { label: "Vendor Approved", status: "green" },
      { label: "JEs Unknown", status: "red" },
    ],
    detail: "Eaton PM (Sarah Chen) confirms: no impact to ship date. Units can redirect to ATL-07 delivery address. Updated shipping labels will reference ATL-07 PO allocation. Vendor approval recorded with timestamp.",
  },
  {
    id: 3,
    label: "Financial Analysis",
    agent: "🧠 AI analyzing journal entries...",
    description: "System identifies all PO-lines requiring journal entries and computes transfer amounts.",
    badges: [
      { label: "14 Units Mapped", status: "green" },
      { label: "Vendor Approved", status: "green" },
      { label: "8 JEs Proposed", status: "amber" },
    ],
    detail: "AI identifies 8 journal entries required: 14 units × $47,200 each = $660,800 total. JE from PHX-03 Task 4400 → ATL-07 Task 4400. Plus 1 JE for proportional freight reallocation ($18,400) and 1 JE for storage costs incurred to date ($6,200). All proposed JEs queued for PM review.",
  },
  {
    id: 4,
    label: "Cascade Complete",
    agent: "✅ All systems updated...",
    description: "Reallocation executed. Production Planner, schedule trackers, financial trackers, and documentation trackers all updated atomically.",
    badges: [
      { label: "14 Units Moved", status: "green" },
      { label: "Trackers Updated", status: "green" },
      { label: "8 JEs Approved", status: "green" },
    ],
    detail: null,
    result: {
      units: "14 PDUs reallocated",
      financial: "$685,400 in JEs processed",
      time: "4 hours vs. 3 weeks manual",
    },
  },
];

const corePrinciples = [
  {
    Icon: Database,
    color: "from-sky-500 to-indigo-500",
    title: "One row per unit",
    body: "Every physical piece of equipment is a single row in the Production Planner. QTY 1, always. A PO for 40 UPS units becomes 40 tracked rows. No exceptions. This is the foundation that makes reallocations, serial number tracking, and per-unit financial allocation possible.",
  },
  {
    Icon: ArrowLeftRight,
    color: "from-violet-500 to-fuchsia-500",
    title: "Reallocations are first-class",
    body: "Not an afterthought. Not a spreadsheet exercise. Reallocations are a governed workflow with vendor approval, line-level tracking, automated financial cascade, and full audit trail. The #1 source of reconciliation pain becomes a one-click operation.",
  },
  {
    Icon: Calculator,
    color: "from-emerald-500 to-teal-500",
    title: "AI-driven journal entries",
    body: "After every reallocation, the system determines which PO-lines need journal entries to which project, computes amounts, and proposes them for human review. Finance teams go from 30-80% of their time on reconciliation to reviewing pre-computed proposals.",
  },
  {
    Icon: Eye,
    color: "from-amber-500 to-orange-500",
    title: "Access control is paramount",
    body: "Vendors see only their Production Planner, only their columns. GCs see project schedules and delivery info. PMs see their assigned projects. Directors see everything. Column-level and row-level filtering — not bolted on, built in from day one.",
  },
  {
    Icon: Brain,
    color: "from-rose-500 to-pink-500",
    title: "The platform remembers",
    body: "Every reallocation, every vendor delay, every schedule gap, every damage claim lands in the same temporal store. After 50 reallocations, the system knows which vendors handle redirects smoothly and which ones don't. Institutional knowledge compounds across all 23 projects.",
  },
  {
    Icon: Clock,
    color: "from-cyan-500 to-emerald-500",
    title: "Proactive, not reactive",
    body: "Daily schedule gap scans. Weekly delivery look-aheads. Monthly Cx milestone reports. Storage duration alerts. The system reaches for the PM with the problem before the PM discovers it — because by then, it's already a crisis.",
  },
];

const comparisonData = {
  headers: ["", "Current State (Smartsheet + Manual)", "OFE Intelligence Platform"],
  rows: [
    ["Production tracking", "Separate Smartsheet per vendor. Manual updates. No cross-vendor view.", "One unified planner. 18 vendors, 23 projects. One row per unit."],
    ["Reallocations", "3+ weeks of manual spreadsheet updates, emails, and reconciliation.", "Single workflow: propose → approve → execute. Cascade in minutes."],
    ["Journal entries", "Finance team manually determines JEs. 30-80% of their time.", "AI proposes JEs automatically. Human reviews and approves."],
    ["Schedule gaps", "PM discovers gap when vendor misses a date.", "Daily automated scan: ROJ vs. ship date across all projects."],
    ["Vendor visibility", "Email-based. Vendor asks PM for status. PM looks up Smartsheet.", "Vendor portal: real-time view of their own production planner."],
    ["Financial tracking", "Manual tracker. Lump sums. Exposure in PM's head.", "Auto-populated. Unit prices enforced. Exposure is a first-class field."],
    ["Documentation", "SharePoint folders. 'Which version is latest?' is a daily question.", "Tagged 'latest'. Linked to serial numbers. Submittal workflow built in."],
    ["Audit trail", "Who changed what? Check email threads and Smartsheet history.", "Every action logged. Policy ID on every mutation. Full chain of custody."],
  ],
};

const alertTypes = [
  { color: "bg-red-500", label: "SCHEDULE_GAP_CRITICAL", example: "PHX-03: 6 Eaton UPS units — ROJ Aug 1, vendor estimated ship Aug 28. Gap: 27 days. No mitigation plan filed." },
  { color: "bg-amber-500", label: "DELIVERY_APPROACHING", example: "ATL-07: 22 Vertiv cooling units shipping in 5 days. 48-hr call-ahead NOT confirmed. GC delivery contact not responding." },
  { color: "bg-violet-500", label: "REALLOCATION_JE_PENDING", example: "Reallocation R-0047: 8 journal entries ($412K) approved by PM, awaiting Finance confirmation. Aging: 6 days." },
  { color: "bg-emerald-500", label: "VENDOR_ON_TIME_ALERT", example: "Schneider Electric on-time performance dropped to 72% this quarter. 14 units at risk across 3 projects. Recommend escalation call." },
  { color: "bg-blue-500", label: "STORAGE_DURATION_WARNING", example: "Blueprint 3PL: 8 PDUs for DEN-02 in storage 94 days. Accrued storage cost: $18,200. Original install date passed." },
  { color: "bg-sky-500", label: "CX_SCOPE_GAP", example: "ATL-07 Phase 2: CxAlloy flagged missing branch circuit monitoring on 4 PDUs. Trigger change order or confirm spec?" },
];

const stakeholders = [
  {
    title: "OFE Director",
    icon: Briefcase,
    blurb: "Cross-portfolio visibility across all 23 projects and 18 vendors. Reallocation decisions backed by real-time schedule and financial data. The single pane of glass that doesn't exist today.",
    policies: ["Reallocation Approval", "Buyout Plan", "Vendor Escalation"],
    value: "One dashboard replaces 40+ Smartsheets. Every reallocation decision shows the cascade impact — schedule, financial, vendor — before you approve it.",
  },
  {
    title: "OFE Project Manager",
    icon: HardHat,
    blurb: "Your projects, your trackers, your vendors — all in one place. Schedule gaps surfaced before they're crises. Financial exposure visible without asking Finance. Submittals tracked without digging through SharePoint.",
    policies: ["Schedule Management", "Financial Control", "Documentation"],
    value: "Stop maintaining spreadsheets. Start managing projects. The system handles data entry, gap detection, and financial reconciliation — you handle decisions.",
  },
  {
    title: "Finance Team",
    icon: Calculator,
    blurb: "Journal entries proposed by AI, reviewed by PMs, confirmed by you. No more manual determination of which PO-lines moved where. The reconciliation burden drops from 80% of your time to reviewing pre-computed proposals.",
    policies: ["Journal Entry Approval", "Budget Allocation", "PFR Reporting"],
    value: "The system does the detective work. You do the verification. Every JE has a source reallocation, a policy ID, and a complete audit trail.",
  },
  {
    title: "Vendor (External)",
    icon: Factory,
    blurb: "Your own portal. Your production planner. Your authorized columns only. Update manufacturing status, ship dates, and production notes directly. No more email chains asking 'where's the latest tracker?'",
    policies: ["Production Updates", "Reallocation Confirmation", "Cx Coordination"],
    value: "Real-time visibility into your commitments. Direct update capability. Faster reallocation approvals. The relationship gets better because the communication gets cleaner.",
  },
  {
    title: "GC / Construction Manager",
    icon: Building2,
    blurb: "Project schedules and delivery dates for your site. ROJ dates you entered flowing directly into gap analysis. Delivery coordination with 48-hour call-ahead tracking. Cx milestone alignment.",
    policies: ["Schedule Coordination", "Delivery Planning", "Cx Integration"],
    value: "You see what's coming to your site and when. The OFE team sees your ROJ dates and builds mitigation plans around them. Both sides work from the same data.",
  },
  {
    title: "ADC Leadership",
    icon: Globe,
    blurb: "Portfolio-level financial and schedule health across the entire OF&E program. Buyout plan approvals with full visibility into vendor mix, lead times, and budget impact. Risk surfaces before it becomes a board conversation.",
    policies: ["OFE Plan Approval", "Budget Oversight", "Risk Monitoring"],
    value: "Governed intelligence over hundreds of millions in equipment spend. Not a slide deck assembled from screenshots — a live, auditable operating system.",
  },
];

const portfolioStats = [
  { value: "23", label: "Active projects" },
  { value: "18", label: "Vendors" },
  { value: "$___M", label: "Equipment under management" },
  { value: "___", label: "Units in production" },
  { value: "94%", label: "Target on-time delivery" },
];

const topProjects = [
  { id: "ATL-07", status: "On Track", units: "312 units", phase: "Phase 2 Cx" },
  { id: "PHX-03", status: "At Risk", units: "248 units", phase: "Production" },
  { id: "DEN-02", status: "On Track", units: "186 units", phase: "Delivery" },
];

const progression = ["Programs", "Production", "Reallocations", "Financials", "Logistics", "Cx"];

const workflowDomains = [
  {
    Icon: Package,
    label: "Vendor Management",
    detail: "18 vendors with full profiles: contacts, quality history, MSA status, factory assessments, SCAR tracking. Org charts per vendor division. Historical performance scoring.",
  },
  {
    Icon: ClipboardList,
    label: "Production Planners",
    detail: "Per-vendor and per-project views. One row per unit. Vendor-specific columns without schema pollution. Cross-vendor search and filter. Bulk import from Oracle PO data.",
  },
  {
    Icon: ArrowLeftRight,
    label: "Reallocation Engine",
    detail: "Multi-step workflow with vendor approval gate. Line-level unit tracking. Automated cascade to schedule, financial, and documentation trackers. AI journal entry proposals.",
  },
  {
    Icon: DollarSign,
    label: "Financial Trackers",
    detail: "Auto-populated from Production Planners + Oracle. Unit price enforcement. Exposure tracking. Change order workflow. PFR code alignment. JE status tracking to completion.",
  },
  {
    Icon: Truck,
    label: "Logistics & Storage",
    detail: "3PL facility directory with insurance tracking. Shipment pipeline. 48-hr call-ahead enforcement. Receiving workflow with damage assessment. Freight cost allocation.",
  },
  {
    Icon: Wrench,
    label: "Cx & Closeout",
    detail: "Monthly look-ahead with milestone dates. CxAlloy integration. Issue tracking with escalation chain. Closeout documentation linked to serial numbers. Latest-version tagging.",
  },
];

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function BadgePill({ label, status }) {
  const colors = {
    red: "border-red-300 bg-red-50 text-red-700",
    amber: "border-amber-300 bg-amber-50 text-amber-700",
    green: "border-emerald-300 bg-emerald-50 text-emerald-700",
  };
  const dots = {
    red: "bg-red-500",
    amber: "bg-amber-500",
    green: "bg-emerald-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${colors[status]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} />
      {label}
    </span>
  );
}

function ReallocationWalkthrough() {
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(false);

  useEffect(() => {
    if (!auto) return;
    if (step >= rescueSteps.length - 1) { setAuto(false); return; }
    const t = setTimeout(() => setStep((s) => s + 1), 2800);
    return () => clearTimeout(t);
  }, [auto, step]);

  const current = rescueSteps[step];
  const isFinal = step === rescueSteps.length - 1;

  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      <div className="space-y-2">
        {rescueSteps.map((s, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={s.id}>
              <button
                onClick={() => { setStep(i); setAuto(false); }}
                className={`w-full rounded-[24px] border p-4 text-left transition-all duration-200 ${
                  active
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-xl dark:border-white dark:bg-white dark:text-zinc-900"
                    : done
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30"
                    : "border-zinc-200 bg-white/80 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/70"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    active ? "bg-white text-zinc-900 dark:bg-zinc-900 dark:text-white"
                    : done ? "bg-emerald-500 text-white"
                    : "bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-400"
                  }`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${active ? "" : done ? "text-emerald-700 dark:text-emerald-400" : "text-zinc-700 dark:text-zinc-300"}`}>
                      {s.label}
                    </div>
                    {s.agent && (
                      <div className={`mt-0.5 text-xs ${active ? "text-zinc-400 dark:text-zinc-600" : "text-zinc-400"}`}>
                        {s.agent}
                      </div>
                    )}
                  </div>
                </div>
              </button>
              {i < rescueSteps.length - 1 && (
                <div className={`ml-[22px] h-2 w-px ${done ? "bg-emerald-300" : "bg-zinc-200 dark:bg-zinc-700"}`} />
              )}
            </div>
          );
        })}
        <div className="flex gap-2 pt-2">
          <Button size="sm" className="rounded-xl" onClick={() => { setStep(0); setAuto(true); }} variant="default">
            ▶ Auto-play
          </Button>
          <Button size="sm" className="rounded-xl" onClick={() => { if (step < rescueSteps.length - 1) setStep((s) => s + 1); }} variant="outline" disabled={step === rescueSteps.length - 1}>
            Next →
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.26 }}
        >
          <Card className={`rounded-[32px] border-2 shadow-xl transition-all duration-500 ${
            isFinal
              ? "border-emerald-400 bg-gradient-to-br from-emerald-950 to-zinc-900 dark:border-emerald-400"
              : "border-zinc-100 bg-gradient-to-br from-white to-zinc-50 dark:border-zinc-800 dark:from-zinc-900 dark:to-zinc-950"
          }`}>
            <CardContent className="p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-zinc-900 p-2.5 text-white dark:bg-zinc-700">
                  <ArrowLeftRight className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Reallocation</div>
                  <div className="font-bold text-zinc-900 dark:text-zinc-100">R-0052 · 14× Eaton 93PM UPS · PHX-03 → ATL-07</div>
                </div>
              </div>

              <div className="mb-5 flex flex-wrap gap-2">
                {current.badges.map((b) => (
                  <motion.div key={b.label} initial={{ scale: 0.92 }} animate={{ scale: 1 }} transition={{ duration: 0.2 }}>
                    <BadgePill label={b.label} status={b.status} />
                  </motion.div>
                ))}
              </div>

              <p className={`mb-4 text-sm leading-7 ${isFinal ? "text-emerald-200" : "text-zinc-600 dark:text-zinc-300"}`}>
                {current.description}
              </p>

              {current.detail && (
                <div className={`rounded-2xl border p-4 text-sm leading-7 ${
                  isFinal
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : "border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300"
                }`}>
                  {current.detail}
                </div>
              )}

              {isFinal && current.result && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mt-5 grid gap-3 sm:grid-cols-3"
                >
                  {[
                    { label: "Equipment", value: current.result.units },
                    { label: "Financial impact", value: current.result.financial },
                    { label: "Time saved", value: current.result.time },
                  ].map((m) => (
                    <div key={m.label} className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                      <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">{m.label}</div>
                      <div className="mt-1 text-sm font-bold text-emerald-300">{m.value}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ScenarioToggle() {
  const [active, setActive] = useState(comparisonData.rows[0][0]);
  const [showAll, setShowAll] = useState(false);

  return (
    <div>
      <div className="mb-6 flex flex-wrap gap-3">
        {comparisonData.rows.slice(0, 4).map((row) => (
          <button
            key={row[0]}
            onClick={() => setActive(row[0])}
            className={`rounded-2xl border px-5 py-2.5 text-left transition-all ${
              active === row[0]
                ? "border-zinc-900 bg-zinc-900 text-white shadow-lg dark:border-white dark:bg-white dark:text-zinc-900"
                : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900"
            }`}
          >
            <div className="text-sm font-semibold">{row[0]}</div>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {comparisonData.rows.filter((r) => r[0] === active).map((row) => (
          <motion.div
            key={row[0]}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.24 }}
            className="grid gap-4 lg:grid-cols-2"
          >
            <Card className="rounded-[28px] border border-red-100 bg-red-50/60 shadow-sm dark:border-red-950 dark:bg-red-950/20">
              <CardContent className="p-6">
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-red-600 dark:text-red-400">Current state</span>
                </div>
                <p className="text-sm leading-7 text-red-800 dark:text-red-300">{row[1]}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[28px] border border-emerald-200 bg-emerald-50/60 shadow-sm dark:border-emerald-900 dark:bg-emerald-950/20">
              <CardContent className="p-6">
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700 dark:text-emerald-400">OFE Platform</span>
                </div>
                <p className="text-sm leading-7 text-emerald-900 dark:text-emerald-200">{row[2]}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="mt-4">
        <button
          onClick={() => setShowAll((v) => !v)}
          className="text-xs font-semibold text-zinc-400 underline underline-offset-2 hover:text-zinc-600 dark:hover:text-zinc-300"
        >
          {showAll ? "Hide full comparison table" : "View all comparisons"}
        </button>
        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 overflow-hidden"
            >
              <div className="overflow-x-auto rounded-[24px] border bg-white shadow-sm dark:bg-zinc-900/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-zinc-50 dark:bg-zinc-800/50">
                      {comparisonData.headers.map((h, i) => (
                        <th key={i} className={`px-6 py-4 text-left font-semibold ${i === 0 ? "text-zinc-500 w-[180px]" : i === 1 ? "text-zinc-600 dark:text-zinc-300" : "text-emerald-700 dark:text-emerald-400"}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.rows.map((row, ri) => (
                      <tr key={ri} className="border-b last:border-0">
                        {row.map((cell, ci) => (
                          <td key={ci} className={`px-6 py-4 ${ci === 0 ? "font-semibold text-zinc-700 dark:text-zinc-300" : ci === 1 ? "text-zinc-500 dark:text-zinc-400" : "font-medium text-zinc-900 dark:text-zinc-100"}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function PortfolioDashboard() {
  return (
    <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
      <CardContent className="p-8 lg:p-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Live Portfolio</p>
          <h3 className="mt-1 text-xl font-bold">OFE Program Dashboard</h3>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.4fr_0.6fr]">
          <div className="space-y-4">
            {/* Vendor performance bars */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Vendor On-Time Performance — This Quarter</p>
              {[
                { vendor: "Eaton", pct: 96, color: "bg-emerald-400" },
                { vendor: "Schneider Electric", pct: 72, color: "bg-amber-400" },
                { vendor: "Vertiv", pct: 91, color: "bg-emerald-400" },
                { vendor: "Cummins", pct: 88, color: "bg-sky-400" },
                { vendor: "Starline (Legrand)", pct: 94, color: "bg-emerald-400" },
              ].map((v) => (
                <div key={v.vendor} className="mb-3 last:mb-0">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-zinc-400">{v.vendor}</span>
                    <span className={`text-xs font-bold ${v.pct >= 90 ? "text-emerald-400" : v.pct >= 80 ? "text-sky-400" : "text-amber-400"}`}>{v.pct}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-white/10">
                    <div className={`h-2 rounded-full ${v.color}`} style={{ width: `${v.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Active reallocations */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Active Reallocations</p>
              {[
                { id: "R-0052", units: "14 UPS", from: "PHX-03", to: "ATL-07", status: "Executing", statusColor: "text-amber-400" },
                { id: "R-0053", units: "8 PDU", from: "DEN-02", to: "PHX-03", status: "Vendor Review", statusColor: "text-sky-400" },
                { id: "R-0054", units: "22 Cooling", from: "ATL-07 Ph1", to: "ATL-07 Ph2", status: "Proposed", statusColor: "text-zinc-400" },
              ].map((r) => (
                <div key={r.id} className="mb-2 flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-3 py-2 last:mb-0">
                  <div>
                    <span className="font-mono text-xs text-zinc-400">{r.id}</span>
                    <span className="ml-2 text-sm text-zinc-300">{r.units}</span>
                    <span className="ml-2 text-xs text-zinc-500">{r.from} → {r.to}</span>
                  </div>
                  <span className={`text-xs font-semibold ${r.statusColor}`}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats column */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            {portfolioStats.map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <div className="text-xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top projects */}
        <div className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Project</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Units</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Phase</th>
              </tr>
            </thead>
            <tbody className="text-zinc-300">
              {topProjects.map((p) => (
                <tr key={p.id} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-2.5 font-mono text-xs">{p.id}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-lg px-2 py-0.5 text-xs font-bold ${p.status === "On Track" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{p.units}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{p.phase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function MondayBriefing() {
  return (
    <Card className="rounded-[32px] border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
      <CardContent className="p-8 lg:p-10">
        <div className="mb-6 border-b border-zinc-200 pb-5 dark:border-zinc-700">
          <div className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
            Weekly OFE Intelligence Briefing
          </div>
          <div className="font-mono text-xs text-zinc-400">Week of June 16, 2026 · Generated 06:00 AM</div>
          <h3 className="mt-2 text-xl font-bold text-zinc-900 dark:text-zinc-100">
            2 schedule gaps critical · 3 reallocations pending · 1 vendor escalation
          </h3>
        </div>

        <div className="mb-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Critical items requiring action this week
          </p>
          <div className="space-y-3">
            {[
              { rank: 1, item: "PHX-03 UPS delivery gap", detail: "6 Eaton UPS units: ROJ Aug 1, vendor estimated ship Aug 28. 27-day gap. Recommend: request Eaton production acceleration or initiate slot swap with DEN-02 units ahead of schedule.", urgency: "CRITICAL", color: "text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900 dark:text-red-400" },
              { rank: 2, item: "ATL-07 Vertiv delivery coordination", detail: "22 cooling units arriving June 23. GC delivery contact unresponsive on 48-hr call-ahead. Risk: site not prepared for receiving. Escalate to CM immediately.", urgency: "HIGH", color: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-400" },
              { rank: 3, item: "Schneider on-time performance", detail: "Q2 on-time dropped to 72%. 14 units affected across PHX-03, ATL-07, DEN-02. Root cause: component supply chain delay at Schneider Monterrey plant. Recommend: executive escalation call with VP of Operations.", urgency: "HIGH", color: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-400" },
            ].map((opp) => (
              <div key={opp.rank} className="flex gap-4 rounded-2xl border border-zinc-100 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-800/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white dark:bg-zinc-100 dark:text-zinc-900">
                  {opp.rank}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{opp.item}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${opp.color}`}>
                      {opp.urgency}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-zinc-500 dark:text-zinc-400">{opp.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Reallocation status
          </p>
          <div className="space-y-2">
            {[
              { dot: "bg-emerald-500", text: "R-0052 (14 UPS, PHX-03 → ATL-07): Vendor approved. JEs proposed. Awaiting PM review of 8 journal entries totaling $685,400." },
              { dot: "bg-amber-500", text: "R-0053 (8 PDU, DEN-02 → PHX-03): Informal vendor review in progress. Eaton response expected by Wednesday." },
              { dot: "bg-zinc-500", text: "R-0054 (22 Cooling, ATL-07 Ph1 → Ph2): Proposed by Director. Internal review phase — no vendor notification yet." },
            ].map((a, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-zinc-100 p-3 dark:border-zinc-800">
                <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${a.dot}`} />
                <p className="text-sm leading-6 text-zinc-700 dark:text-zinc-300">{a.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-3 dark:border-zinc-700 dark:bg-zinc-800/50">
          <p className="font-mono text-xs text-zinc-500">
            Portfolio summary: <span className="font-bold text-zinc-700 dark:text-zinc-300">23 active projects</span> ·{" "}
            <span className="font-bold text-zinc-700 dark:text-zinc-300">18 vendors</span> ·{" "}
            <span className="font-bold text-zinc-700 dark:text-zinc-300">3 reallocations in flight</span> ·{" "}
            <span className="font-bold text-zinc-700 dark:text-zinc-300">Overall on-time: 89%</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function StageButton({ stage, active, onClick, showConnector }) {
  const { Icon } = stage;
  const isActive = active.id === stage.id;
  return (
    <>
      <button
        onClick={() => onClick(stage)}
        className={`w-full rounded-[28px] border p-5 text-left transition-all duration-200 ${
          isActive
            ? "border-zinc-900 bg-zinc-900 text-white shadow-2xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/70 dark:hover:border-zinc-600"
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`shrink-0 rounded-2xl bg-gradient-to-br ${stage.color} p-3 text-white shadow-lg`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isActive ? "opacity-50" : "text-zinc-400"}`}>
                  {stage.num}
                </div>
                <div className="mt-0.5 text-[17px] font-semibold leading-tight">{stage.label}</div>
              </div>
              <ArrowRight className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isActive ? "translate-x-1 opacity-60" : "opacity-30"}`} />
            </div>
            <p className={`mt-1.5 text-sm leading-relaxed ${isActive ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"}`}>
              {stage.sub}
            </p>
          </div>
        </div>
      </button>
      {showConnector && <div className="ml-[30px] h-5 w-px bg-zinc-200 dark:bg-zinc-700" />}
    </>
  );
}

function DetailPanel({ stage }) {
  const { Icon } = stage;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={stage.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.26 }} className="h-full">
        <Card className="h-full rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-50 shadow-xl dark:from-zinc-900 dark:to-zinc-950">
          <CardContent className="p-8 lg:p-10">
            <div className="mb-6 flex items-center gap-4">
              <div className={`rounded-3xl bg-gradient-to-br ${stage.color} p-4 text-white shadow-xl`}>
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Stage {stage.num}</p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">{stage.label}</h3>
              </div>
            </div>
            <p className="mb-8 text-sm leading-7 text-zinc-600 dark:text-zinc-300">{stage.summary}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {stage.bullets.map((item) => (
                <div key={item} className={`flex items-start gap-3 rounded-2xl border ${stage.lightBorder} ${stage.lightBg} p-4 dark:border-zinc-800 dark:bg-zinc-900/50`}>
                  <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`} />
                  <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{item}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

function StoryView({ activeStage, setActiveStage }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      <div>
        {stages.map((stage, idx) => (
          <StageButton key={stage.id} stage={stage} active={activeStage} onClick={setActiveStage} showConnector={idx < stages.length - 1} />
        ))}
      </div>
      <DetailPanel stage={activeStage} />
    </div>
  );
}

function StakeholderView() {
  const [selected, setSelected] = useState(stakeholders[0].title);
  const current = stakeholders.find((s) => s.title === selected) || stakeholders[0];
  const Icon = current.icon;
  return (
    <div className="grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
      <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
        <CardContent className="p-6 lg:p-8">
          <div className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Stakeholders</div>
          <div className="space-y-3">
            {stakeholders.map((item) => {
              const AI = item.icon;
              const active = item.title === selected;
              return (
                <button key={item.title} onClick={() => setSelected(item.title)} className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${active ? "border-white/20 bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                  <div className="rounded-2xl bg-white/10 p-2.5"><AI className="h-5 w-5" /></div>
                  <div>
                    <div className="font-semibold">{item.title}</div>
                    <div className="mt-1 text-sm leading-6 text-zinc-300">{item.blurb}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.title}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="grid gap-6"
        >
          <Card className="rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-100 shadow-2xl dark:from-zinc-900 dark:to-zinc-950">
            <CardContent className="p-8 lg:p-10">
              <div className="flex items-center gap-4">
                <div className="rounded-3xl bg-zinc-900 p-4 text-white shadow-xl dark:bg-white dark:text-zinc-900">
                  <Icon className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Stakeholder view</div>
                  <h3 className="mt-1 text-3xl font-semibold tracking-tight">{current.title}</h3>
                </div>
              </div>

              <p className="mt-6 max-w-3xl text-base leading-7 text-zinc-600 dark:text-zinc-300">{current.value}</p>

              <div className="mt-8 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">Policy domains</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {current.policies.map((policy) => (
                      <Badge key={policy} className="rounded-full px-3 py-1 text-sm">{policy}</Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">Why this view matters</div>
                  <p className="mt-4 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                    Each stakeholder sees the same equipment data through a different lens. The Director sees portfolio risk and reallocation impact. The vendor sees their production commitments. Finance sees journal entries. Same data, governed by policy — not separate spreadsheets maintained by different people.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {stakeholders.map((item, i) => {
              const SmallIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  viewport={{ once: true }}
                >
                  <Card className={`h-full rounded-[28px] border shadow-sm ${item.title === current.title ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "bg-white/80 dark:bg-zinc-900/70"}`}>
                    <CardContent className="p-5">
                      <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${item.title === current.title ? "bg-white/10 dark:bg-zinc-200" : "bg-zinc-100 dark:bg-zinc-800"}`}>
                        <SmallIcon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                      <p className={`mt-2 text-sm leading-6 ${item.title === current.title ? "text-zinc-200 dark:text-zinc-700" : "text-zinc-600 dark:text-zinc-300"}`}>
                        {item.blurb}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function PathwayGrid({ pathways, title, color }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500">{title}</h4>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pathways.map((pw, i) => {
          const { Icon } = pw;
          return (
            <motion.div key={pw.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }} viewport={{ once: true }}>
              <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-5">
                  <div className={`mb-3 inline-flex rounded-2xl bg-gradient-to-br ${color} p-2.5 text-white shadow-md`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <h5 className="mb-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">{pw.label}</h5>
                  <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{pw.detail}</p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

export default function OFEStory() {
  const [activeStage, setActiveStage] = useState(stages[0]);
  const [view, setView] = useState("story");

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">

        {/* ── #1 PRE-HERO HOOK ─────────────────────────────────── */}
        <section className="pt-10 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="rounded-[24px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="px-8 py-6 lg:px-10">
                <p className="text-lg leading-8 text-zinc-200 italic">
                  "We reallocated 14 UPS units across two projects. It took three weeks of spreadsheet updates, vendor emails, and manual journal entries. One of the JEs was wrong and we didn't catch it for two months."
                </p>
                <p className="mt-3 text-sm font-semibold text-zinc-500">
                  This system does that reallocation in four hours — with the journal entries pre-computed.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── #2 HERO ──────────────────────────────────────────── */}
        <section className="py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge className="mb-6 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
                From spreadsheet chaos to governed intelligence
              </Badge>
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl">
                <span className="whitespace-nowrap">Every unit.</span>{" "}
                <span className="text-zinc-400">Every vendor.</span>
                <br />
                <span className="whitespace-nowrap">Every dollar.</span>{" "}
                <span className="text-zinc-400">One system.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-zinc-500 dark:text-zinc-400">
                AI-powered OF&E lifecycle management for hyperscale data center construction.
                18 vendors, 23 projects, hundreds of millions in equipment — tracked per unit,
                reallocated with financial cascade, governed by policy, visible to every stakeholder
                with exactly the access they need.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {progression.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className="rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-sm dark:bg-zinc-900">{step}</div>
                    {i < progression.length - 1 && <ArrowRight className="h-4 w-4 text-zinc-400" />}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <Card className="rounded-[28px] border-0 bg-zinc-900 text-white shadow-2xl">
                <CardContent className="p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">Core thesis</p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    OF&E procurement for hyperscale data centers isn't a purchasing problem — it's a
                    coordination problem. Equipment constantly reallocates between projects. Budgets follow
                    the equipment. Schedules shift. Documentation must track. And 18 vendors, 23 projects,
                    and multiple GCs all need to see exactly the right information at the right time.
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    No spreadsheet survives this. The system that does must track every unit individually,
                    orchestrate reallocations as a governed workflow, and compute journal entries automatically.
                  </p>
                  <p className="text-sm leading-7 text-zinc-300">
                    That's what this is. One system. Every unit. Every dollar. Every stakeholder.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* ── CREDIBILITY STRIP ────────────────────────────────── */}
        <section className="border-y border-zinc-200 py-5 dark:border-zinc-800">
          <p className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-zinc-400">
            Built on eosai.ai — the enterprise AI infusion layer powering OT security, construction intelligence, and capital markets.
          </p>
        </section>

        {/* ── #3 THE PAIN — COST OF STATUS QUO ─────────────────── */}
        <section className="py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-[32px] border-0 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white shadow-2xl">
              <CardContent className="p-10 lg:p-14 text-center">
                <p className="text-xl font-semibold leading-9 text-zinc-300 sm:text-2xl">
                  The current process costs more than anyone has quantified.
                </p>
                <div className="mx-auto mt-8 grid max-w-4xl gap-6 sm:grid-cols-4">
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-red-400 lg:text-5xl">30-80%</div>
                    <div className="mt-1 text-sm text-zinc-400">of Finance team time on manual JE reconciliation</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-amber-400 lg:text-5xl">3+ wks</div>
                    <div className="mt-1 text-sm text-zinc-400">per reallocation — manual spreadsheet cascade</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-zinc-300 lg:text-5xl">40+</div>
                    <div className="mt-1 text-sm text-zinc-400">separate Smartsheets maintained manually</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-zinc-300 lg:text-5xl">0</div>
                    <div className="mt-1 text-sm text-zinc-400">cross-vendor, cross-project visibility today</div>
                  </div>
                </div>
                <p className="mt-8 text-lg font-semibold text-emerald-400">
                  Every one of these becomes a solved problem.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── 01 / SYSTEM PROGRESSION ──────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <SectionLabel index={1}>System Progression</SectionLabel>
              <h2 className="text-3xl font-bold tracking-tight">Six domains. One platform.</h2>
              <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
                Each domain covers a phase of the OF&E lifecycle. The platform core — auth, policy engine,
                audit trail, multi-model AI, and the React shell — never changes. Only the domain intelligence expands.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setView("story")} className="rounded-2xl px-5" variant={view === "story" ? "default" : "outline"}>System View</Button>
              <Button onClick={() => setView("stakeholder")} className="rounded-2xl px-5" variant={view === "stakeholder" ? "default" : "outline"}>Stakeholder View</Button>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={view} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
              {view === "story" ? <StoryView activeStage={activeStage} setActiveStage={setActiveStage} /> : <StakeholderView />}
            </motion.div>
          </AnimatePresence>
        </section>

        {/* ── #5 ANIMATED REALLOCATION WALKTHROUGH ─────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={2}>Reallocation Walkthrough</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Watch 14 UPS units move between projects in four hours.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Today this takes three weeks of manual updates across multiple spreadsheets, vendor emails,
              and finance reconciliation. The system orchestrates the entire cascade — production planner updates,
              vendor approval, schedule tracker adjustments, financial reallocation, and journal entry generation — as one governed workflow.
            </p>
          </div>
          <ReallocationWalkthrough />
        </section>

        {/* ── 03 / CORE PRINCIPLES ─────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={3}>Core Principles</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The six commitments that make this work.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              These aren't features — they're architectural decisions. Each one eliminates a class of problem
              that no amount of spreadsheet discipline can solve.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {corePrinciples.map((pillar, i) => {
              const { Icon } = pillar;
              return (
                <motion.div key={pillar.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.06 }} viewport={{ once: true }}>
                  <Card className="h-full rounded-[28px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-6">
                      <div className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br ${pillar.color} p-3 text-white shadow-lg`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">{pillar.title}</h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">{pillar.body}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 04 / THE INVERSION — BEFORE VS AFTER ─────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={4}>The Transformation</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Current state vs. OFE Platform.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Pick a pain point. See how the system solves it. Then expand the full table for the complete comparison.
            </p>
          </div>
          <ScenarioToggle />
        </section>

        {/* ── 05 / DOMAIN DEEP DIVE ────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={5}>Domain Deep Dive</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Six integrated domains. Zero silos.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every domain shares the same database, the same policy engine, the same audit trail, and the same
              AI reasoning layer. A reallocation in Production automatically cascades to Financials, Logistics, and Cx.
            </p>
          </div>
          <PathwayGrid
            pathways={workflowDomains}
            title="OFE Skill — Integrated Domain Architecture"
            color="from-sky-500 to-indigo-500"
          />
        </section>

        {/* ── 06 / 24/7 INTELLIGENCE ───────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={6}>Proactive Intelligence</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The system that reaches for you.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Daily schedule gap scans. Weekly delivery look-aheads. Monthly Cx milestone reports.
              Vendor performance alerts. Storage duration warnings. The platform surfaces problems
              before they become crises.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {alertTypes.map((alert, i) => (
              <motion.div key={alert.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.05 }} viewport={{ once: true }}>
                <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                  <CardContent className="p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${alert.color}`} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{alert.label}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{alert.example}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── 07 / MONDAY MORNING BRIEFING ─────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={7}>Weekly Briefing</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What your team gets every Monday at 6 AM.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Machine-generated intelligence briefing. Critical schedule gaps. Reallocation status.
              Vendor performance flags. Everything the Director needs to start the week — without
              opening a single spreadsheet.
            </p>
          </div>
          <MondayBriefing />
        </section>

        {/* ── 08 / PORTFOLIO DASHBOARD ─────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={8}>Portfolio View</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The operating system for OF&E.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every project, every vendor, every reallocation, every financial metric — one live dashboard.
              Not a screenshot assembled quarterly. A real-time, governed, auditable view of hundreds of millions
              in equipment spend.
            </p>
          </div>
          <PortfolioDashboard />
        </section>

        {/* ── 09 / DECISION SURFACE — JE REVIEW ───────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={9}>Decision Surface</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What Finance actually sees.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Not a spreadsheet of PO numbers to reconcile. A pre-computed list of journal entries with source
              reallocation, affected PO-lines, from/to projects, amounts, and one-click approval. The AI does
              the detective work. The human does the verification.
            </p>
          </div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
            <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-8 lg:p-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 p-3 text-white shadow-lg">
                    <Calculator className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Journal Entry Review</p>
                    <h3 className="text-xl font-bold">Reallocation R-0052 · PHX-03 → ATL-07</h3>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">PO-Line</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">From Project</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">To Project</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-zinc-400">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-zinc-400">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {[
                        { po: "4892-03", desc: "Eaton 93PM UPS (units 1-6)", from: "PHX-03", to: "ATL-07", amount: "$283,200", status: "Pending" },
                        { po: "4892-09", desc: "Eaton 93PM UPS (units 7-14)", from: "PHX-03", to: "ATL-07", amount: "$377,600", status: "Pending" },
                        { po: "4892-17", desc: "Freight (proportional)", from: "PHX-03", to: "ATL-07", amount: "$18,400", status: "Pending" },
                        { po: "5011-02", desc: "Blueprint storage (accrued)", from: "PHX-03", to: "ATL-07", amount: "$6,200", status: "Pending" },
                      ].map((row) => (
                        <tr key={row.po} className="border-b border-white/5">
                          <td className="px-4 py-3 font-mono text-xs">{row.po}</td>
                          <td className="px-4 py-3">{row.desc}</td>
                          <td className="px-4 py-3 text-red-400">{row.from}</td>
                          <td className="px-4 py-3 text-emerald-400">{row.to}</td>
                          <td className="px-4 py-3 text-right font-semibold">{row.amount}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="rounded-lg bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-400">{row.status}</span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-white/5">
                        <td colSpan={4} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-zinc-400">Total</td>
                        <td className="px-4 py-3 text-right text-lg font-bold text-emerald-400">$685,400</td>
                        <td className="px-4 py-3 text-center">
                          <span className="rounded-lg bg-emerald-500/20 px-3 py-1 text-xs font-bold text-emerald-400">Approve All</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="mt-3 text-xs text-zinc-600">
                  AI-generated journal entry proposals from Reallocation R-0052. Each line traces back to a specific
                  PO-line, a specific set of planner items, and the reallocation directive. PM reviewed. Finance confirms.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Audit chain</p>
                    <div className="space-y-2 text-sm text-zinc-300">
                      <p><span className="font-semibold text-zinc-400">Initiated:</span> Director (J. Martinez) · June 12, 10:14 AM</p>
                      <p><span className="font-semibold text-zinc-400">Vendor approved:</span> Eaton (S. Chen) · June 12, 2:30 PM</p>
                      <p><span className="font-semibold text-zinc-400">JEs proposed:</span> AI · June 12, 2:31 PM</p>
                      <p><span className="font-semibold text-zinc-400">PM reviewed:</span> PHX-03 PM (K. Williams) · June 12, 4:15 PM</p>
                      <p><span className="font-semibold text-emerald-400">Awaiting:</span> Finance confirmation</p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">vs. Manual process</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Time to identify JEs</span>
                        <span className="font-bold text-emerald-400">1 minute vs. 2 weeks</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Accuracy</span>
                        <span className="font-bold text-emerald-400">100% traceable vs. "we think"</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Audit trail</span>
                        <span className="font-bold text-emerald-400">Complete vs. email threads</span>
                      </div>
                      <div className="mt-2 border-t border-white/10 pt-3">
                        <p className="text-xs text-zinc-500">
                          The AI doesn't make the decision. It assembles the evidence and proposes. The human verifies and approves.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── 10 / ACCESS CONTROL ──────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={10}>Access Control</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Every stakeholder sees exactly what they should.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Access control isn't a feature — it's the foundation. Vendor users see their Production Planner
              with their authorized columns. GC users see project schedules and delivery info. PMs see their
              assigned projects. Directors see everything. Column-level and row-level filtering, enforced at the API layer.
            </p>
          </div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
            <div className="overflow-x-auto rounded-[24px] border bg-white shadow-sm dark:bg-zinc-900/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-zinc-50 dark:bg-zinc-800/50">
                    <th className="px-6 py-4 text-left font-semibold text-zinc-500">User Type</th>
                    <th className="px-6 py-4 text-left font-semibold text-zinc-600 dark:text-zinc-300">Sees</th>
                    <th className="px-6 py-4 text-left font-semibold text-zinc-600 dark:text-zinc-300">Can Edit</th>
                    <th className="px-6 py-4 text-left font-semibold text-zinc-600 dark:text-zinc-300">Auth</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { type: "OFE Director", sees: "All projects, all vendors, all financials", edits: "Reallocations, plan approvals", auth: "Internal SSO" },
                    { type: "OFE PM", sees: "Assigned projects only", edits: "Trackers, submittals, financials", auth: "Internal SSO" },
                    { type: "Finance", sees: "Financial trackers, POs, JE tracking", edits: "JE status, budget confirmations", auth: "Internal SSO" },
                    { type: "Vendor (External)", sees: "Own production planner, authorized columns only", edits: "Mfg status, dates, vendor notes", auth: "Customer IdP" },
                    { type: "GC (External)", sees: "Project schedule, delivery info", edits: "ROJ dates, delivery addresses", auth: "Customer IdP" },
                    { type: "Leadership", sees: "Everything (read-only)", edits: "OFE plan approvals, buyout approvals", auth: "Internal SSO" },
                  ].map((row) => (
                    <tr key={row.type} className="border-b last:border-0">
                      <td className="px-6 py-4 font-semibold text-zinc-700 dark:text-zinc-300">{row.type}</td>
                      <td className="px-6 py-4 text-zinc-500 dark:text-zinc-400">{row.sees}</td>
                      <td className="px-6 py-4 text-zinc-500 dark:text-zinc-400">{row.edits}</td>
                      <td className="px-6 py-4">
                        <span className={`rounded-lg px-2 py-0.5 text-xs font-bold ${row.auth === "Customer IdP" ? "bg-violet-100 text-violet-700 dark:bg-violet-950/30 dark:text-violet-400" : "bg-sky-100 text-sky-700 dark:bg-sky-950/30 dark:text-sky-400"}`}>
                          {row.auth}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </section>

        {/* ── 11 / INTEGRATION ARCHITECTURE ────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={11}>Integration Architecture</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Connects to everything. Replaces the gaps.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              The platform integrates with your existing systems — Oracle for POs and financials, Smartsheet for
              migration, SharePoint for documents, CxAlloy for commissioning — while replacing the manual coordination
              layer that ties them together.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Database, label: "Oracle ERP", detail: "REST API integration. PO data flows in. JE completions flow back. REQ submission from within the platform.", color: "from-red-500 to-orange-500" },
              { icon: ClipboardList, label: "Smartsheet", detail: "API-based migration. Bulk import of existing Production Planners, Schedule Trackers, Financial Trackers. Then Smartsheet becomes read-only archive.", color: "from-emerald-500 to-teal-500" },
              { icon: FileText, label: "SharePoint", detail: "Document references, not copies. PO filings, submittals, O&Ms linked by URL. SharePoint stays the file store. The platform provides the tracking layer.", color: "from-sky-500 to-blue-500" },
              { icon: Wrench, label: "CxAlloy", detail: "API integration for commissioning milestone sync. Bidirectional: installation status from CxAlloy, scope gaps trigger change orders in the platform.", color: "from-violet-500 to-purple-500" },
              { icon: Warehouse, label: "Blueprint (3PL)", detail: "Storage facility management. Insurance tracking. Inventory movement sync — initially manual, API integration when available.", color: "from-amber-500 to-yellow-500" },
              { icon: Users, label: "Customer Identity Provider", detail: "External users (vendors, GCs) authenticate through your identity provider. SSO integration. No separate credentials to manage.", color: "from-pink-500 to-rose-500" },
            ].map((item, i) => {
              const { icon: ItemIcon } = item;
              return (
                <motion.div key={item.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }} viewport={{ once: true }}>
                  <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-5">
                      <div className={`mb-3 inline-flex rounded-2xl bg-gradient-to-br ${item.color} p-2.5 text-white shadow-md`}>
                        <ItemIcon className="h-4 w-4" />
                      </div>
                      <h5 className="mb-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">{item.label}</h5>
                      <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{item.detail}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 12 / BUILD PHASES ────────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={12}>Build Phases</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Deployed in phases.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Each phase produces a deployable increment. The schema, API,
              and core tools are live in Phase 1.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                phase: "Phase 1",
                title: "Foundation",
                items: ["Full schema + migrations (20 tables)", "Pydantic models with role-based view variants", "OFE router with CRUD for all 6 domains", "25 function-calling tools registered", "Access control middleware"],
                color: "border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/20",
                dotColor: "bg-sky-500",
              },
              {
                phase: "Phase 2",
                title: "Core Workflows",
                items: ["PO explosion (QTY N → N individual rows)", "Reallocation multi-step workflow", "AI journal entry identification", "Schedule gap analysis + mitigation suggestions"],
                color: "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20",
                dotColor: "bg-amber-500",
              },
              {
                phase: "Phase 3",
                title: "UI + External Portal",
                items: ["OFE Dashboard + Production Planner views", "Project Dashboard (schedule/financial/docs tabs)", "Reallocation Workspace + JE Review page", "Vendor and GC external portal with filtered views"],
                color: "border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/20",
                dotColor: "bg-violet-500",
              },
              {
                phase: "Phase 4",
                title: "Documentation + Logistics + Cx",
                items: ["Submittal workflow + closeout documentation", "Storage and shipment tracking + receiving workflow", "Cx Look-Ahead + CxAlloy integration", "All 7 proactive scheduler jobs wired"],
                color: "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20",
                dotColor: "bg-emerald-500",
              },
              {
                phase: "Phase 5",
                title: "Integration",
                items: ["Smartsheet API bulk migration", "Oracle REST API bidirectional sync", "Program Design Requirements (BOM lookup)", "Contextual reminder engine ('DID YOU REMEMBER')"],
                color: "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/20",
                dotColor: "bg-rose-500",
              },
              {
                phase: "Phase 6",
                title: "Hardening",
                items: ["90% test coverage (matching Forge standard)", "All 16 policies registered in PolicyDB", "Audit trail verification on every mutation", "Column-level access control testing", "Load testing for production-scale planners"],
                color: "border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/20",
                dotColor: "bg-zinc-500",
              },
            ].map((phase, i) => (
              <motion.div key={phase.phase} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.05 }} viewport={{ once: true }}>
                <Card className={`rounded-[24px] border ${phase.color} shadow-sm`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`mt-1 h-3 w-3 shrink-0 rounded-full ${phase.dotColor}`} />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{phase.phase}: {phase.title}</span>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          {phase.items.map((item) => (
                            <div key={item} className="flex items-start gap-2">
                              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                              <span className="text-sm text-zinc-600 dark:text-zinc-300">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── CLOSING FRAME ────────────────────────────────────── */}
        <section className="pt-8 pb-4">
          <Card className="rounded-[32px] border-0 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white shadow-2xl">
            <CardContent className="p-8 lg:p-10">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Closing frame</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">
                What if every reallocation took four hours instead of three weeks?
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-300">
                This isn't a tool that replaces your team. It's a system that gives your team back the 30-80%
                of their time currently spent on spreadsheet maintenance, manual reconciliation, and email-based
                coordination. The PM manages projects instead of data. Finance verifies instead of investigates.
                The Director decides instead of waits.
              </p>
              <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-300">
                18 vendors. 23 projects. Hundreds of millions in equipment. One system. Every unit tracked.
                Every dollar traced. Every stakeholder seeing exactly what they need.
              </p>
              <p className="mt-4 text-base leading-7 text-zinc-400">
                The coordination problem is the whole problem. This solves it.
              </p>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}
